// Things related to Uploads page
const Uploads = {};
// Total number of files being sent to the cloud
// Includes files being uploaded or processed
// in some way before or after the upload
// It is the total number of all files
// in all uploads. Each upload can contain
// more than one file so if there are
// e.g. 2 uploads going on at the same
// time and they have 2 and 5 files
// then this number should be 7
Uploads.totalNrOfFilesBeingSent = 0;
// Number of uploading tasks that are processing
// or waiting to be processed
Uploads.nrOfOngoingUploadTasks = 0;
// Info on the ongoing upload tasks
Uploads.ongoingUploadTasks = {};
// This is set to true while data is being encrypted or sent to s3.
// Only one such process is allowed at a time.
// Not sure if there would be performance benefits of doing multiple uploads
// at the same time since we are already using multipart upload,
// but there might be benefits in doing the encryption part
// for multiple upload tasks at the same time, so that's something
// to consider in the future.
Uploads.isUploading = false;
Uploads.updateUploadingMessages = function(error) {
    CP.updateUploadingMessages(error, Uploads.totalNrOfFilesBeingSent, Uploads.nrOfOngoingUploadTasks);
};
/**
 * Maps unsupported source file extensions to the CAD application they belong to.
 * We keep this as a single source of truth so the validation logic works
 * consistently for files, folders, drag-and-drop, and zip contents.
 */ Uploads.unsupportedCadSoftwareByExtension = {
    // Revit
    '.rvt': 'Revit',
    '.rfa': 'Revit',
    // Rhino
    '.3dm': 'Rhino',
    '.3ds': 'Rhino',
    '.dwg': 'Rhino',
    '.fbx': 'Rhino',
    '.usd': 'Rhino',
    // SketchUp
    '.skp': 'SketchUp',
    '.dae': 'SketchUp',
    // Archicad
    '.pln': 'Archicad',
    '.pla': 'Archicad',
    '.bpn': 'Archicad',
    // Navisworks
    '.nwc': 'Navisworks',
    '.nwd': 'Navisworks',
    '.nwf': 'Navisworks',
    '.ifc': 'Navisworks'
};
// Return the CAD application associated with a path's extension, or null if the
// path is not one of the known unsupported source file types.
Uploads.getUnsupportedCadSoftwareForPath = function(path) {
    const lowerPath = (path || '').toLowerCase();
    if (!lowerPath || lowerPath.endsWith('/')) return null;
    const dotIndex = lowerPath.lastIndexOf('.');
    if (dotIndex < 0) return null;
    const ext = lowerPath.substring(dotIndex);
    return Uploads.unsupportedCadSoftwareByExtension[ext] || null;
};
/**
 * Check a list of file paths for unsupported CAD source formats.
 * Returns the first detected CAD application name, or null if none are found.
 * Logs every detected unsupported path to the console.
 */ Uploads.getUnsupportedCadSoftwareFromPaths = function(paths) {
    if (!Array.isArray(paths) || paths.length === 0) return null;
    let firstFoundSoftware = null;
    let firstFoundPath = null;
    for(let i = 0; i < paths.length; i++){
        const path = paths[i] || '';
        const software = Uploads.getUnsupportedCadSoftwareForPath(path);
        if (!software) continue;
        if (!firstFoundSoftware) {
            firstFoundSoftware = software;
            firstFoundPath = path;
        }
        console.warn(`Upload contains unsupported ${software} source file: ${path}`);
    }
    if (!firstFoundSoftware) return null;
    console.error(`Upload rejected due to unsupported ${firstFoundSoftware} source file: ${firstFoundPath}`);
    return firstFoundSoftware;
};
Uploads.showCADFileErrorMessage = function(cadSoftware) {
    const dedupKey = 'cadFileError:' + cadSoftware;
    // Use the shared dedup Set so we don't show duplicate messages for the same CAD software
    if (CP.MsgManager.activeErrorMessages.has(dedupKey)) return;
    CP.MsgManager.activeErrorMessages.add(dedupKey);
    const template = CP.getel('cadFileErrorMessageTemplate');
    if (!template || !template.content) {
        const fallbackMessage = `${cadSoftware} models must first be opened in ${cadSoftware} and then exported to the PC version of Arkio using our plugins.`;
        const bubble = new CP.TextBubble(CP.MsgManager.errorArea, fallbackMessage, CP.TextBubbleStyle.Error, 'Error', true);
        bubble.onDismiss = ()=>CP.MsgManager.activeErrorMessages.delete(dedupKey);
        return;
    }
    const container = document.createElement('div');
    container.appendChild(template.content.cloneNode(true));
    container.querySelectorAll('[data-cad-software]').forEach((el)=>{
        el.textContent = cadSoftware;
    });
    const bubble = new CP.TextBubble(CP.MsgManager.errorArea, "", CP.TextBubbleStyle.Error, 'Error', true);
    bubble.setText(container.innerHTML, true);
    bubble.onDismiss = ()=>CP.MsgManager.activeErrorMessages.delete(dedupKey);
};
// --- Directory traversal helpers (for folder drag-and-drop) ---
// The functions below make it possible to support dropping whole folders in
// browsers that expose the (non-standard but widely supported) WebKit directory
// APIs. We keep this logic self-contained so the rest of the upload pipeline
// can continue to work with a single, normalized representation: an array of
// { file: File, path: string } where `path` is a safe, normalized, relative
// path using forward slashes. This ensures:
//
// - Zipping can preserve folder structure by adding files at `path`.
// - Main-file detection can continue to operate on basenames (last path segment).
// - Security: we never allow dangerous paths (e.g., '..') inside the archive.
// Normalize a relative path: forward slashes, no leading slash, collapse repeats, remove . and .. safely
// Normalizes a relative path string coming from drag-and-drop traversal.
// Rationale:
// - Browsers may provide different path styles (backslashes on Windows,
//   multiple separators, or leading slashes). We standardize to POSIX-style
//   forward slashes.
// - We also resolve '.' and '..' segments to protect against path traversal
//   and to keep paths canonical within the zip.
// - We intentionally strip leading slashes to keep all paths relative.
Uploads.normalizeRelativePath = function(path) {
    // Delegate to shared util to keep behavior consistent across modules
    return Util.normalizeRelativePath(path);
};
// Reads all entries from a FileSystemDirectoryReader.
// Important nuance: readEntries() returns entries in batches and MUST be called
// repeatedly until an empty array is returned. Failing to loop will miss files.
Uploads.readAllEntries = async function(directoryReader) {
    const all = [];
    while(true){
        const batch = await new Promise((resolve, reject)=>{
            try {
                directoryReader.readEntries(resolve);
            } catch (e) {
                reject(e);
            }
        });
        if (!batch || batch.length === 0) break;
        for(let i = 0; i < batch.length; i++)all.push(batch[i]);
    }
    return all;
};
// Recursively traverses a FileSystemEntry tree and collects files.
// Parameters:
// - entry: FileSystemEntry (file or directory)
// - currentPath: normalized relative path of the current directory within the dropped root
// - files: array to push results into; each result is { file: File, path: string }
// Design choices:
// - We build paths from the dropped root so multi-root drops (multiple folders)
//   still produce sane, distinct paths in the final list.
// - We normalize every path segment as we go, so upstream callers do not need to.
Uploads.traverseEntry = async function(entry, currentPath, files) {
    if (!entry) return;
    try {
        if (entry.isFile) {
            // entry.file uses a callback-based API; wrap in a Promise for async/await ergonomics
            const file = await new Promise((resolve, reject)=>entry.file(resolve, reject));
            const rel = Uploads.normalizeRelativePath((currentPath ? currentPath + '/' : '') + file.name);
            files.push({
                file: file,
                path: rel
            });
            return;
        }
        if (entry.isDirectory) {
            const nextPath = Uploads.normalizeRelativePath((currentPath ? currentPath + '/' : '') + entry.name);
            const reader = entry.createReader();
            // Some browsers return directory entries in multiple batches
            const children = await Uploads.readAllEntries(reader);
            for(let i = 0; i < children.length; i++)await Uploads.traverseEntry(children[i], nextPath, files);
        }
    } catch (err) {
        // We fail soft here: log and continue traversal of other entries
        console.error('Directory traversal error:', err);
    }
};
// Builds a flat list of { file, path } from a DataTransfer object.
// Behavior:
// - If the browser supports item.webkitGetAsEntry(), we traverse the directory
//   tree(s) and produce normalized relative paths prefixed by the dropped root.
// - If not supported, we fall back to dataTransfer.files (flat list), using
//   file.webkitRelativePath when available (e.g., from <input webkitdirectory>).
Uploads.getFilesFromDataTransfer = async function(dataTransfer) {
    const out = [];
    try {
        const dtFiles = dataTransfer && dataTransfer.files ? Array.from(dataTransfer.files) : [];
        const dtItems = dataTransfer && dataTransfer.items ? Array.from(dataTransfer.items) : [];
        // Detect if any directory entries are present in items
        let hasDirectory = false;
        for(let i = 0; i < dtItems.length; i++){
            const it = dtItems[i];
            if (it.kind !== 'file') continue;
            const entry = it.webkitGetAsEntry ? it.webkitGetAsEntry() : null;
            if (entry && entry.isDirectory) {
                hasDirectory = true;
                break;
            }
        }
        if (!hasDirectory && dtFiles.length > 0) // Prefer files for pure file drops (preserves webkitRelativePath when available)
        for(let i = 0; i < dtFiles.length; i++){
            const f = dtFiles[i];
            out.push({
                file: f,
                path: f.webkitRelativePath || f.name
            });
        }
        else {
            // Folder-capable or no files: traverse items
            for(let i = 0; i < dtItems.length; i++){
                const item = dtItems[i];
                if (item.kind !== 'file') continue;
                const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : null;
                if (entry) await Uploads.traverseEntry(entry, '', out);
                else {
                    const file = item.getAsFile ? item.getAsFile() : null;
                    if (file) out.push({
                        file: file,
                        path: file.name
                    });
                }
            }
            // Merge in top-level files from DataTransfer.files that were not captured
            // by traversal. Filter out likely directory placeholders (size === 0).
            if (dtFiles.length > 0) {
                const seen = new Set(out.map((x)=>{
                    const f = x.file;
                    return f ? `${f.name}|${f.size}|${f.lastModified || 0}|${f.type || ''}` : '';
                }));
                for(let i = 0; i < dtFiles.length; i++){
                    const f = dtFiles[i];
                    // Skip probable directory placeholders with zero size
                    if (!f || !f.name || f.size === 0) continue;
                    const key = `${f.name}|${f.size}|${f.lastModified || 0}|${f.type || ''}`;
                    if (!seen.has(key)) {
                        out.push({
                            file: f,
                            path: f.webkitRelativePath || f.name
                        });
                        seen.add(key);
                    }
                }
            }
        }
    } catch (err) {
        console.error('Error while extracting files from DataTransfer:', err);
    }
    return out;
};
// Analyze selected items (by path) to infer content type and pick a main file.
//
// Input:
// - paths: array of strings; each string is a relative path using forward slashes
//          (e.g., "Project/Models/house.glb" or just "image.jpg").
//
// Output object:
// - mainFile : The basename of the main file (e.g., 'model.obj'). We return a basename
//              so downstream code that computes entry IDs and names the .arkiocloud file
//              continues to behave exactly as before.
// - icon     : A value from Constants.Icon indicating resource category (Models/Images).
// - zipFile  : Name of a .zip file if found among the provided paths. NOTE: we store the
//              plain filename (basename) here because later matching is done against
//              File.name. The stricter rule that only a single top-level .zip should
//              trigger the "existing zip" branch is handled elsewhere (step 8).
Uploads.getFileDataInfo = function(paths) {
    var info = {};
    var modelMainFile = "";
    var imageMainFile = "";
    info.zipFile = "";
    // Supported files: JPG, PNG, OBJ, GLB, GLTF, ZIP
    var n = paths.length;
    var i;
    for(i = 0; i < n; i++){
        // Work with the basename and lowercase for case-insensitive checks
        var fullPath = paths[i] || '';
        var lastSlash = fullPath.lastIndexOf('/');
        var base = lastSlash >= 0 ? fullPath.substring(lastSlash + 1) : fullPath;
        var name = base;
        var lower = (name || '').toLowerCase();
        // If there are are more than one .glb .gltf or .obj file then the first file
        // found will be considered the "main" file.
        // Same for image files.
        // An image file will only be considered the main file if there was no
        // .glb .gltf or .obj file uploaded.
        if (lower.endsWith(".zip")) {
            info.zipFile = name; // keep the plain filename here for matching a File later
            break;
        }
        if (lower.endsWith(".glb") || lower.endsWith(".gltf") || lower.endsWith(".obj")) modelMainFile = name;
        if (imageMainFile === "") {
            if (lower.endsWith(".jpg") || lower.endsWith(".jpeg") || lower.endsWith(".png")) imageMainFile = name;
        }
    }
    if (info.zipFile === "") {
        if (modelMainFile !== "") {
            info.mainFile = modelMainFile;
            info.icon = Constants.Icon.Models;
        } else if (imageMainFile !== "") {
            info.mainFile = imageMainFile;
            info.icon = Constants.Icon.Images;
        } else info.icon = "";
    }
    return info;
};
Uploads.uploadToCloud_nativeEncryptStreamTest = async ()=>{
    if (!CP.isLinked()) {
        CP.goToGroupPage();
        return;
    }
    let selectedFiles = CP.getel('fileInput').files;
    let file = selectedFiles[0];
    let fileAsStream = file.stream();
    let encryptionKey = CP.retrieveEncryptionKey()?.groupID;
    console.log('encryption key: ' + encryptionKey);
    let hash = CryptoUtil.createRandomHash();
    console.log('hash: ' + hash);
    console.log('Encrypting stream');
    let encryptedStream = await CryptoUtil.nativeEncryptStream(window.crypto.subtle, encryptionKey, hash, fileAsStream);
    console.log('Got encrypted stream. Converting it to buffer');
    let encryptedStreamAsBuffer = await DataUtil.streamToBuffer(encryptedStream);
    console.log(encryptedStreamAsBuffer);
    CP.saveBufferToDisk(encryptedStreamAsBuffer, "data");
};
// Example showing how to do encryption with Web Crypto API
Uploads.uploadToCloud_NativeCryptoTest = async ()=>{
    if (!CP.isLinked()) {
        CP.goToGroupPage();
        return;
    }
    let selectedFiles = CP.getel('fileInput').files;
    let file = selectedFiles[0];
    let fileAsStream = file.stream();
    let bytes = await DataUtil.streamToBuffer(fileAsStream);
    console.log('bytes');
    console.log(bytes);
    let encryptionKey = CP.retrieveEncryptionKey()?.groupID;
    let hash = CryptoUtil.createRandomHash();
    console.log('Using native encryption');
    let encrypted = await CryptoUtil.nativeEncrypt(window.crypto.subtle, encryptionKey, hash, bytes);
    encrypted = new Uint8Array(encrypted);
    console.log(encrypted);
    let stream2 = file.stream();
    console.log('Encrypting stream');
    let encryptedStream = await CryptoUtil.nativeEncryptStream(window.crypto.subtle, encryptionKey, hash, stream2);
    console.log('Got encrypted stream. Converting it to buffer');
    let encryptedStreamAsBuffer = await DataUtil.streamToBuffer(encryptedStream);
    console.log(encryptedStreamAsBuffer);
    CP.saveBufferToDisk(encrypted, "data");
    console.log('Using native decryption');
    let decrypted = await CryptoUtil.nativeDecrypt(window.crypto.subtle, encryptionKey, hash, encrypted);
    decrypted = new Uint8Array(decrypted);
    console.log(decrypted);
    console.log('encrypting using crypto js');
    let encrypted2 = CryptoUtil.encryptObjectData(encryptionKey, hash, bytes);
    console.log(encrypted2);
};
// Example showing how to use TransformStream
// TODO remove this 
Uploads.uploadToCloud_TransformStreamTest = async ()=>{
    if (!CP.isLinked()) {
        CP.goToGroupPage();
        return;
    }
    let selectedFiles = CP.getel('fileInput').files;
    let file = selectedFiles[0];
    let fileAsStream = file.stream();
    console.log('creating TransformStream');
    const uppercaseTransform = new TransformStream({
        async transform (chunk, controller) {
            console.log('calling transform');
            const text = new TextDecoder().decode(chunk);
            const uppercasedText = text.toUpperCase();
            controller.enqueue(new TextEncoder().encode(uppercasedText));
        }
    });
    console.log('calling pipeThrough');
    let uppercaseStream = fileAsStream.pipeThrough(uppercaseTransform);
    console.log('calling streamToBuffer');
    let buffer = await DataUtil.streamToBuffer(uppercaseStream);
    console.log(buffer);
    const textOutput = new TextDecoder().decode(buffer);
    console.log(textOutput);
};
// Uploads files to the cloud. If filesOverride is provided (e.g., from a drag-and-drop event),
// those files will be used; otherwise we fall back to the hidden input element.
// Using an optional parameter keeps one single upload pipeline for both browse and drag/drop
Uploads.uploadToCloud = async (filesOverride)=>{
    if (!CP.isLinked()) {
        CP.goToGroupPage();
        return;
    }
    // Unify inputs into a single representation: [{ file: File, path: string }]
    // - Drag-and-drop may pass File[] or [{ file, path }]
    // - <input webkitdirectory> yields a FileList with webkitRelativePath
    let items = [];
    const fileListFromInput = CP.getel('fileInput').files;
    const src = filesOverride || fileListFromInput;
    if (Array.isArray(src)) {
        if (src.length > 0 && typeof src[0] === 'object' && 'file' in src[0] && 'path' in src[0]) items = src;
        else items = src.map((f)=>({
                file: f,
                path: f && f.webkitRelativePath ? f.webkitRelativePath : f ? f.name : ''
            }));
    } else if (src && typeof src === 'object' && 'length' in src) items = Array.from(src).map((f)=>({
            file: f,
            path: f.webkitRelativePath || f.name
        }));
    var n = items.length;
    let nrOfFiles = n;
    // Value that was added to Uploads.totalNrOfFilesBeingSent
    // in case it needs to be removed
    let nrOfFilesAddedInThisTask = 0;
    if (n < 1) {
        CP.showErrorMessage("No files selected!");
        return;
    }
    // Create a random id for the upload job
    // We used to use this for the entry IDs but now we
    // are making them using a different method.
    const taskID = CryptoUtil.createRandomHash();
    console.log('Entry hash: ' + taskID);
    // Centralized cleanup helper to avoid counter drift and duplicate logic.
    // Defined outside try/catch so both blocks can call it.
    let cleanupDone = false;
    const endUploadTask = function(errorHappened) {
        if (cleanupDone) return;
        if (Uploads.ongoingUploadTasks[taskID] != null) {
            delete Uploads.ongoingUploadTasks[taskID];
            if (Uploads.nrOfOngoingUploadTasks > 0) Uploads.nrOfOngoingUploadTasks -= 1;
            if (nrOfFilesAddedInThisTask > 0) Uploads.totalNrOfFilesBeingSent -= nrOfFilesAddedInThisTask;
        }
        Uploads.updateUploadingMessages(!!errorHappened);
        cleanupDone = true;
    };
    try {
        Uploads.ongoingUploadTasks[taskID] = {};
        Uploads.nrOfOngoingUploadTasks += 1;
        Uploads.updateUploadingMessages(false);
        console.log("Reading " + n + " files...");
        // Decide whether to treat selection as a single existing .zip upload.
        // Rule: only if there is exactly ONE top-level item and its name ends with .zip (case-insensitive).
        // - Top-level means the normalized path contains no '/'.
        // - If a .zip is present inside a dropped folder, we IGNORE this branch and create a new zip instead.
        const singleItemPath = n === 1 ? items[0].path || items[0].file?.name || '' : '';
        const isTopLevel = singleItemPath.indexOf('/') === -1;
        const isZipName = singleItemPath.toLowerCase().endsWith('.zip');
        const isSingleTopLevelZip = n === 1 && isTopLevel && isZipName;
        // Early size validation: sum sizes across unified items before any heavy work
        var totalBytes = 0;
        for(var i = 0; i < n; i++)totalBytes += items[i].file?.size || 0;
        let maxBytes1 = 2000000000;
        let maxBytes2 = 500000000;
        let maxBytes3 = isSingleTopLevelZip ? 50000000 : 200000000;
        if (totalBytes > maxBytes1) {
            endUploadTask(true);
            console.error('Size of files greater than ' + maxBytes1);
            CP.showErrorMessage("Size of upload too large! Must not exceed 2 GB.");
            return;
        } else if (totalBytes > maxBytes2) {
            console.warn('Size of files greater than ' + maxBytes2);
            const dedupKey = 'uploadLargeFileWarning500';
            if (!CP.MsgManager.activeWarningMessages.has(dedupKey)) {
                CP.MsgManager.activeWarningMessages.add(dedupKey);
                const bubble = new CP.TextBubble(CP.MsgManager.errorArea, "", CP.TextBubbleStyle.Warning, 'Warning', true);
                bubble.setText(CP.getel('uploadLargeWarningTemplate').innerHTML, true);
                bubble.onDismiss = ()=>CP.MsgManager.activeWarningMessages.delete(dedupKey);
            }
        } else if (totalBytes > maxBytes3) {
            const filemessage = CP.getel(isSingleTopLevelZip ? 'uploadLargeWarningTemplate50' : 'uploadLargeWarningTemplate200').innerHTML;
            console.warn('Size of files greater than ' + maxBytes3);
            const dedupKey = isSingleTopLevelZip ? 'uploadLargeFileWarning50' : 'uploadLargeFileWarning200';
            if (!CP.MsgManager.activeWarningMessages.has(dedupKey)) {
                CP.MsgManager.activeWarningMessages.add(dedupKey);
                const bubble = new CP.TextBubble(CP.MsgManager.errorArea, "", CP.TextBubbleStyle.Warning, 'Warning', true);
                bubble.setText(filemessage, true);
                bubble.onDismiss = ()=>CP.MsgManager.activeWarningMessages.delete(dedupKey);
            }
        }
        // Build paths list for type inference (basenames derived inside)
        let paths = [];
        for(let i = 0; i < items.length; i++)paths.push(items[i].path || items[i].file?.name || '');
        // Get type of resource and main file from the provided paths
        let fileInfo = Uploads.getFileDataInfo(paths);
        // Zip handling rule:
        // - Accept exactly one top-level .zip; reject all other cases where any .zip is present.
        //   Rationale: the Arkio client automatically extracts zip files placed in the import
        //   folder, so allowing .zip files mixed with other files (or multiple/nested zips)
        //   leads to confusion and oversized/nested archives. There is no need to include
        //   .zip files alongside other files.
        const containsAnyZip = items.some((i)=>(i.path || i.file?.name || '' || '').toLowerCase().endsWith('.zip'));
        if (containsAnyZip && !isSingleTopLevelZip) {
            endUploadTask(true);
            let msg = "Uploading .zip files together with other files is not supported. Please upload files or folders without any .zip file or a single .zip file.";
            console.error(msg);
            CP.showErrorMessage(msg);
            return;
        }
        // For normal file/folder uploads we validate the selected paths directly.
        // For single top-level zip uploads we defer validation until after the zip
        // archive has been opened so we can inspect the files inside it instead of
        // only validating the outer .zip file.
        if (!isSingleTopLevelZip) {
            const unsupportedCadSoftware = Uploads.getUnsupportedCadSoftwareFromPaths(paths);
            if (unsupportedCadSoftware) {
                endUploadTask(true);
                Uploads.showCADFileErrorMessage(unsupportedCadSoftware);
                return;
            }
        }
        let infoFileWithoutExt = "info";
        let arkioCloudExt = ".arkiocloud";
        // TODO add some info in here to be used in Arkio
        let cloudInfo = JSON.stringify({
        });
        let icon = null; // Icon for cloud entry, inferred from the uploaded files
        var zipUInt8arr = null;
        if (isSingleTopLevelZip) try {
            // There is a single top-level .zip; load and process it directly
            let nInner = items.length;
            for(let i = 0; i < nInner; i++)if (items[i].file && items[i].file.name?.toLowerCase().endsWith('.zip')) {
                // Load the zip archive
                let zipArchive = await DataUtil.loadZipArchive(items[i].file);
                // Get the name of the files in the zip archive
                let filenames = Object.keys(zipArchive.files);
                nrOfFiles = filenames.length;
                // Validate the files inside the uploaded zip. This uses the
                // same path-based helper as ordinary uploads so browse, drag and
                // drop, folders, and zip contents all follow one validation rule.
                const unsupportedCadSoftware = Uploads.getUnsupportedCadSoftwareFromPaths(filenames);
                if (unsupportedCadSoftware) {
                    endUploadTask(true);
                    Uploads.showCADFileErrorMessage(unsupportedCadSoftware);
                    return;
                }
                fileInfo = Uploads.getFileDataInfo(filenames);
                if (fileInfo.zipFile !== '') {
                    // We don't support zip files inside zip files
                    // If we encounter this, we reject the upload
                    endUploadTask(true);
                    let msg = "It's not supported to upload a zip file inside a zip file!";
                    console.error(msg);
                    CP.showErrorMessage(msg);
                    return;
                }
                if (fileInfo.mainFile != null && fileInfo.mainFile !== "") infoFileWithoutExt = Util.getFilenameWithoutExtension(fileInfo.mainFile);
                let cloudInfoFileName = infoFileWithoutExt + arkioCloudExt;
                //Adding .arkiocloud file inside the zip file
                let zipContent = await DataUtil.addFileToZip(zipArchive, cloudInfoFileName, cloudInfo);
                icon = await DataUtil.inferIconFromZip(zipContent);
                zipUInt8arr = new Uint8Array(zipContent);
                break;
            }
        } catch (err) {
            endUploadTask(true);
            let msg = "Failed when reading zip file!";
            console.error(msg + ": ", err);
            CP.showErrorMessage(msg);
            return;
        }
        else {
            if (fileInfo.mainFile != null && fileInfo.mainFile !== "") infoFileWithoutExt = Util.getFilenameWithoutExtension(fileInfo.mainFile);
            // .arkiocloud file
            let cloudInfoFileName = infoFileWithoutExt + arkioCloudExt;
            // Pass only File objects to icon inference to keep compatibility
            icon = await DataUtil.inferIconFromFiles(items.map((i)=>i.file));
            // If no zip file was uploaded then we need to zip the files
            // that got uploaded
            try {
                console.log("Compressing files...");
                // Create a zip archive (preserve folder structure via `path`)
                // Also adds the .arkiocloud file as an extra file at the zip root
                zipUInt8arr = await DataUtil.zipFiles(items, cloudInfoFileName, cloudInfo);
                const zipFileSize = zipUInt8arr.byteLength;
                console.log('Done zipping files. Size of zip is ' + zipFileSize);
            } catch (err) {
                endUploadTask(true);
                let msg = "Failed to compress files!";
                console.error(msg + ": ", err);
                CP.showErrorMessage(msg);
                return;
            }
        }
        nrOfFilesAddedInThisTask = nrOfFiles;
        Uploads.totalNrOfFilesBeingSent += nrOfFilesAddedInThisTask;
        Uploads.updateUploadingMessages(false);
        if (icon != null) fileInfo.icon = icon;
        console.log("Icon: " + fileInfo.icon);
        console.log("Resource main file: " + fileInfo.mainFile);
        // Reject if no main file (.gltf/.glb/.obj or image) or invalid icon
        if (!fileInfo.mainFile || fileInfo.icon == '') {
            endUploadTask(true);
            CP.showErrorMessage("No supported main file found. Please upload .glb, .gltf, .obj files or supported images.");
            return;
        }
        Uploads.ongoingUploadTasks[taskID].Filename = fileInfo.mainFile;
        var groupInfo = await CP.retrieveEncryptionKeyAndCheckVersion();
        // Wait for other uploads to finish
        // before starting this one
        while(Uploads.isUploading)await Util.sleep(200);
        Uploads.isUploading = true;
        const entryID = CryptoUtil.createHashBase64OfString(fileInfo.mainFile);
        var putRes = await Cloud.putObject(groupInfo, entryID, fileInfo, nrOfFiles, zipUInt8arr, function(message) {
            console.log(message);
        });
        endUploadTask(!putRes.success);
        if (putRes.success) console.log("Done uploading.");
        else CP.showErrorMessage("Upload to cloud failed.");
    } catch (e) {
        // Centralized failure cleanup
        try {
            endUploadTask(true);
        } catch (_) {}
        CP.showErrorMessage("Encountered an unexpected error during upload.");
    } finally{
        // Always release the uploading gate even if unexpected errors occur
        Uploads.isUploading = false;
    }
};
Uploads.fillTable = async function() {
    let tableBody = CP.getel('UploadsTableBody');
    let entryFilterFunc = function(entry) {
        if (entry.entryValue.File) return entry.entryValue.File.ArkioImport != null;
        if (entry.entryValue.Resource) return true;
        // Exclude deprecated ACC automation job entries
        return false;
    };
    let fillTableFunc = function(tableBod, tableDat) {
        CP.updateTableWithData(tableBod, tableDat, {
            enableRemove: true,
            showFileCount: true
        }, {
            onErr: CP.showErrorMessage,
            onWarn: CP.showWarningMessage
        });
        // Show an empty-state dashed drop hint when there are no entries;
        // hide it when entries exist. This gives a clear affordance for drag-and-drop
        // without altering the existing table rendering logic.
        try {
            const emptyHint = CP.getel('emptyDropHint');
            const tableEl = CP.getel('UploadsTable');
            const showEmpty = tableDat.length === 0;
            if (emptyHint) CP.setVisibility(emptyHint, showEmpty);
            if (tableEl) CP.setVisibility(tableEl, !showEmpty);
        } catch (err) {
            // Non-fatal: if the hint cannot be toggled we still render the table
            console.error('Failed to toggle empty drop hint: ', err);
        }
    };
    CP.getEntriesAndFillTable(tableBody, entryFilterFunc, Uploads.ongoingUploadTasks, fillTableFunc);
};
Uploads.bodyOnLoad = async function() {
    CP.initMsgManager(CP.getel('errorArea'), null, null); //TODO add more arguments here when ready
    await CP.initPage();
    // Initialize shared content for empty panel and overlay
    Uploads.initDropPanels();
    // Initialize drag-and-drop handlers after the page is ready
    Uploads.initDragAndDrop();
    Util.repeatForever(Uploads.fillTable, Constants.TableUpdateDelay);
};
// Initialize drag-and-drop behavior for the uploads drop zone only (not the whole page).
// Listeners are scoped to the drop zone container so the rest of the page
// remains interactive (buttons/links stay visible and clickable), while the overlay
// covers only the table area.
Uploads.initDragAndDrop = function() {
    const overlay = CP.getel('dropOverlay');
    const dropZone = CP.getel('uploadsDropZone');
    const emptyHint = CP.getel('emptyDropHint');
    if (!dropZone || !overlay) return; // If markup is missing, abort gracefully
    let dragCounter = 0;
    // Centralized definition of min-height class (single source of truth).
    // This maps to a CSS rule in src/assets/styles/main.css.
    Uploads.dragMinHeightClass = Uploads.dragMinHeightClass || 'dropzone-minh';
    // Helper: does this drag event carry files?
    const hasFiles = (e)=>{
        try {
            return Array.from(e.dataTransfer?.types || []).includes('Files');
        } catch  {
            return false;
        }
    };
    const prevent = (e)=>{
        e.preventDefault();
        e.stopPropagation();
    };
    const showOverlay = ()=>{
        overlay.classList.remove('hidden');
        // Grow the container during drag so the overlay stays within bounds
        dropZone.classList.add(Uploads.dragMinHeightClass);
    };
    const hideOverlay = ()=>{
        overlay.classList.add('hidden');
        dropZone.classList.remove(Uploads.dragMinHeightClass);
    };
    // Scope listeners to the drop zone so the overlay only appears there
    dropZone.addEventListener('dragenter', (e)=>{
        if (hasFiles(e)) {
            dragCounter++;
            showOverlay();
        }
        prevent(e);
    }, false);
    dropZone.addEventListener('dragover', (e)=>{
        if (hasFiles(e)) showOverlay();
        prevent(e);
    }, false);
    dropZone.addEventListener('dragleave', (e)=>{
        if (hasFiles(e)) {
            dragCounter = Math.max(0, dragCounter - 1);
            if (dragCounter === 0) hideOverlay();
        }
        prevent(e);
    }, false);
    dropZone.addEventListener('drop', async (e)=>{
        if (!hasFiles(e)) return;
        prevent(e);
        // If more than one top-level folder is dropped, cancel with a user-facing error
        try {
            const dtItems = e.dataTransfer && e.dataTransfer.items ? Array.from(e.dataTransfer.items) : [];
            let dirCount = 0;
            for(let i = 0; i < dtItems.length; i++){
                const it = dtItems[i];
                if (it.kind !== 'file') continue;
                const entry = it.webkitGetAsEntry ? it.webkitGetAsEntry() : null;
                if (entry && entry.isDirectory) dirCount++;
            }
            if (dirCount > 1) {
                // Reset drag state and inform the user
                dragCounter = 0;
                hideOverlay();
                CP.showErrorMessage("Dropping multiple folders at once is not supported. Please drop one folder at a time, or drop a parent folder containing the subfolders.");
                return;
            }
        } catch (_) {}
        // Build a normalized list from the DataTransfer:
        // - On folder-capable browsers, this yields [{ file, path }] with paths relative to the dropped root
        // - Else, falls back to a flat list using file names (no subfolders)
        let items = [];
        try {
            items = await Uploads.getFilesFromDataTransfer(e.dataTransfer);
        } catch (err) {
            console.error('Failed to extract files from drop event:', err);
            items = [];
        }
        // Always hide overlay and reset the counter even if something failed above
        dragCounter = 0;
        hideOverlay();
        if (items && items.length > 0) await Uploads.uploadToCloud(items);
        else // Provide user feedback if extraction yielded no files
        CP.showErrorMessage("Failed to read dropped files or directories.");
    }, false);
// Intentionally do not open the file dialog on click in drop areas;
// the dedicated "Browse file(s)" button handles browsing.
};
// Render shared content into a target panel using a template to keep UI consistent
Uploads.renderDropPanelContent = function(targetEl) {
    try {
        if (!targetEl) return;
        const tpl = document.getElementById('dropPanelTemplate');
        if (!tpl || !tpl.content || !tpl.content.firstElementChild) return;
        targetEl.innerHTML = '';
        targetEl.appendChild(tpl.content.firstElementChild.cloneNode(true));
    } catch (err) {
        console.error('Failed to render drop panel content:', err);
    }
};
// Initialize both the empty-state panel and the overlay with the same shared content
Uploads.initDropPanels = function() {
    try {
        const empty = CP.getel('emptyDropHint');
        Uploads.renderDropPanelContent(empty);
        // Ensure empty state has same min-height as drag state (single source of truth)
        if (empty) empty.classList.add(Uploads.dragMinHeightClass);
        Uploads.renderDropPanelContent(CP.getel('dropOverlay'));
    } catch (err) {
        console.error('Failed to init drop panels:', err);
    }
};

//# sourceMappingURL=repo.538c54fc.js.map
